<template>
  <v-container id="timeline-label-for-cefr-enumerator" class="d-flex flex-column">
    <v-data-table-with-auto-scroll
      id="timeline-label-for-cefr-table"
      ref="timelineLabelForCefrTable"
      class="mt-1 d-flex flex-column"
      dense hide-default-footer fixed-header
      v-bind:auto-scroll.sync="$data.$_enablesAutoScroll"
      v-bind:headers="$_timelineSegmentHeaders"
      v-bind:items="$_allSelectedTimelineSegmentItems"
      v-bind:items-per-page="$_allSelectedTimelineSegmentItems.length"
      v-bind:search="$data.$_filterKeyword"
      v-bind:target-idcs="$_scrollTargetIdcs"
      v-on:click:row="$_onClickRow"
      v-on:current-items="$_onCurrentItems"
    >
      <template v-slot:top>
        <div class="d-flex flex-row align-end mb-1">
          <v-switch
            dense hide-details
            label="Auto Scroll"
            class="mr-3"
            v-model="$data.$_enablesAutoScroll"
            v-on:keydown.stop
          >
          </v-switch>
          <v-select
            dense multiple hide-details
            label="Timeline Data ID"
            class="mr-3"
            v-model="$data.$_currentTimelineDataIds"
            v-bind:items="$_timelineDataIdAndNames"
            v-on:keydown.stop
          >
          </v-select>
          <v-text-field
            dense hide-details
            label="Filter"
            v-model="$data.$_filterKeyword"
          >
          </v-text-field>
        </div>
      </template>

      <template v-slot:item.beginMsec="{ item }">
        {{ item.beginString }}
      </template>

      <template v-slot:item.endMsec="{ item }">
        {{ item.endString }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex flex-nowrap">
          <v-btn
            icon small class="flex-nowrap"
            v-on:click.stop="$_openDialog(item)"
          >
            <v-icon small>mdi-dock-window</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table-with-auto-scroll>
  </v-container>
</template>

<style scoped>
#timeline-label-for-cefr-enumerator,
#timeline-label-for-cefr-table {
  height: 100%;
}
</style>

<script>
import {
  TimelineDataSet,
  TimelineSegmentId,
  MarkerSegmentData,
  EditorDefinition,
  VDataTableWithAutoScroll,
} from '@Scatt'
import LabelForCefrSegmentData from '../../modules/segmentDataTypes/LabelForCefrSegmentData.js'

const conversationTopicTimelineDataName = 'Conversation Topic';

export default {
  editorDefinition: new EditorDefinition(
    'TimelineLabelForCefrEnumerator',
    'Transcription',
    timelineDataSet => {
      for (let timelineData of Object.values(timelineDataSet)) {
        if (timelineData.segmentDataType === LabelForCefrSegmentData) return true;
      }
      return false;
    },
  ),

  components: {
    VDataTableWithAutoScroll,
  },

  watch: {
    $_conversationTopicTimelineDataId: {
      async handler(conversationTopicTimelineDataId) {
        if (conversationTopicTimelineDataId !== undefined) return;
        let newTimelineDataId = await this.generateNewHiddenTimelineData(
          MarkerSegmentData,
          conversationTopicTimelineDataName,
          true,
        );
        this.unhideTimelineData(newTimelineDataId);
      },
      immediate: true,
    },

    timelineDataSet: {
      handler() {
        if (this.$_conversationTopicTimelineDataId === undefined) return;
        this.$_updateConversationTopicTimelineData(this.$_conversationTopicTimelineDataId);
      },
      immediate: true,
      deep: true,
    },

    '$data.$_lastPlayTimeMsec'(lastPlayTimeMsec) {
      this.$_updateTableState(this.$data.$_filteredTimelineSegmentItems, lastPlayTimeMsec);
    },

    $_allSelectedTimelineSegmentItems() { this.$_resetScrollTarget() },
  },

  props: {
    timelineDataSet: { type: Object },
  },

  data() {
    return {
      $_lastPlayTimeMsec: 0,
      $_onPlayInfoUpdatedCallbackId: undefined,
      $_enablesAutoScroll: true,
      $_currentTimelineDataIds: [],
      $_filterKeyword: '',
      $_scrollTargetFirstRowIdx: undefined,
      $_scrollTargetLastRowIdx: undefined,
      $_markedRowIdcs: [],
      $_filteredTimelineSegmentItems: [],
    };
  },

  computed: {
    $_conversationTopicTimelineDataId() {
      for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
        if (timelineData.segmentDataType !== MarkerSegmentData) continue;
        if (timelineData.name !== conversationTopicTimelineDataName) continue;
        return timelineDataId;
      }
      return undefined;
    },

    $_labelForCefrTimelineDataSet() {
      let labelForCefrTimelineDataSet = new TimelineDataSet();
      for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
        if (timelineData.segmentDataType === LabelForCefrSegmentData) {
          labelForCefrTimelineDataSet[timelineDataId] = timelineData;
        }
      }
      return labelForCefrTimelineDataSet;
    },

    $_timelineDataIdAndNames() {
      return Object.entries(this.$_labelForCefrTimelineDataSet).map(
        ([ timelineDataId, timelineData ]) => ({
          text: '[' + timelineDataId + '] ' + timelineData.name,
          value: timelineDataId,
        }),
      );
    },

    $_timelineSegmentHeaders() {
      return [
        { value: 'beginMsec',        text: 'Begin',    sortable: false },
        { value: 'endMsec',          text: 'End',      sortable: false },
        { value: 'timelineDataName', text: 'Timeline', sortable: false },
        { value: 'label',            text: 'Label',    sortable: false },
        { value: 'topic',            text: 'Topic',    sortable: false },
        { value: 'actions',                            sortable: false },
      ];
    },

    $_allSelectedTimelineSegmentItems() {
      if (!this.$data.$_currentTimelineDataIds) return [];
      let timelineSegmentItems = [];
      for (let timelineDataId of this.$data.$_currentTimelineDataIds) {
        let timelineData = this.timelineDataSet[timelineDataId];
        for (let [ segmentId, segment ] of Object.entries(timelineData.segments)) {
          let timelineSegmentItem = {
            begin: segment.begin,
            beginMsec: (segment.begin === null)? -Infinity : segment.begin.msec,
            beginString: (segment.begin === null)? '' : segment.begin.toFormatString(),
            end: segment.end,
            endMsec: (segment.end === null)? Infinity : segment.end.msec,
            endString: (segment.end === null)? '' : segment.end.toFormatString(),
            timelineDataName: timelineData.name,
            label: segment.data.label,
            topic: segment.data.topic,
            timelineSegmentId: new TimelineSegmentId(timelineDataId, segmentId),
          };
          let insertIdx = timelineSegmentItems.findIndex(
            existingTimelineSegmentItem => timelineSegmentItem.beginMsec < existingTimelineSegmentItem.beginMsec
          );
          if (insertIdx === -1) {
            timelineSegmentItems.push(timelineSegmentItem);
          } else {
            timelineSegmentItems.splice(insertIdx, 0, timelineSegmentItem);
          }
        }
      }
      return timelineSegmentItems;
    },

    $_scrollTargetIdcs() {
      if (this.$data.$_scrollTargetFirstRowIdx === undefined) return undefined;
      if (this.$data.$_scrollTargetLastRowIdx === undefined) return undefined;
      if (this.$data.$_scrollTargetFirstRowIdx === this.$data.$_scrollTargetLastRowIdx) return [ this.$data.$_scrollTargetFirstRowIdx ];
      return [ this.$data.$_scrollTargetFirstRowIdx, this.$data.$_scrollTargetLastRowIdx ];
    },
  },

  inject: [
    'setSegment',
    'setNewSegment',
    'deleteSegments',
    'generateNewHiddenTimelineData',
    'unhideTimelineData',
    'registerOnPlayInfoUpdatedCallback',
    'unregisterOnPlayInfoUpdatedCallback',
    'invokeActionOnSelectedTimelineSegments',
    'selectTimelineSegments',
    'clearSelectedTimelineSegments',
    'selectTimelineSegmentAndSeek',
  ],

  created() {
    this.$data.$_onPlayInfoUpdatedCallbackId = this.registerOnPlayInfoUpdatedCallback(this.$_onPlayInfoUpdatedCallback);
    this.$data.$_currentTimelineDataIds = Object.keys(this.$_labelForCefrTimelineDataSet);
    this.$data.$_filteredTimelineSegmentItems = this.$_allSelectedTimelineSegmentItems;
  },

  beforeDestroy() {
    this.unregisterOnPlayInfoUpdatedCallback(this.$data.$_onPlayInfoUpdatedCallbackId);
  },

  methods: {
    /* private */
    $_onClickRow(event) { this.selectTimelineSegmentAndSeek(event.timelineSegmentId) },

    $_onPlayInfoUpdatedCallback(playInfo) {
      if (playInfo.playTime.msec < this.$data.$_lastPlayTimeMsec) {
        this.$_resetScrollTarget();
      }
      this.$data.$_lastPlayTimeMsec = playInfo.playTime.msec;
    },

    $_onCurrentItems(filteredTimelineSegmentItems) {
      this.$data.$_filteredTimelineSegmentItems = filteredTimelineSegmentItems;
      // wait DOM updated
      this.$nextTick(() => { this.$_updateTableState(filteredTimelineSegmentItems, this.$data.$_lastPlayTimeMsec) });
    },

    $_openDialog(event) {
      this.clearSelectedTimelineSegments();
      this.selectTimelineSegments(event.timelineSegmentId);
      this.invokeActionOnSelectedTimelineSegments('editSegment');
    },

    $_resetScrollTarget() {
      this.$data.$_scrollTargetFirstRowIdx = undefined;
      this.$data.$_scrollTargetLastRowIdx = undefined;
    },

    $_updateConversationTopicTimelineData(conversationTopicTimelineDataId) {
      let topicSegments = {};
      for (let labelForCefrTimelineData of Object.values(this.$_labelForCefrTimelineDataSet)) {
        for (let segment of Object.values(labelForCefrTimelineData.segments)) {
          let topic = segment.data.topic;
          if (!Object.keys(topicSegments).includes(topic)) {
            topicSegments[topic] = MarkerSegmentData.generateEmpty(segment.begin, segment.end);
            topicSegments[topic].data.name = topic;
          } else {
            if (topicSegments[topic].begin !== null) {
              if (topicSegments[topic].begin.isGreaterThan(segment.begin)) {
                topicSegments[topic].begin.assign(segment.begin);
              }
            }
            if (topicSegments[topic].end !== null) {
              if (topicSegments[topic].end.isLessThan(segment.end)) {
                topicSegments[topic].end.assign(segment.end);
              }
            }
          }
        }
      }

      let existingConversationTopicTimelineData = this.timelineDataSet[conversationTopicTimelineDataId];
      let existingConversationTopicSegmentIds = Object.keys(existingConversationTopicTimelineData.segments);
      let updatedConversationTopicSegmentIds = [];
      for (let [ topic, labelSegment ] of Object.entries(topicSegments)) {
        let existingTopicSegmentId = existingConversationTopicSegmentIds.find(existingSegmentId => {
          return existingConversationTopicTimelineData.segments[existingSegmentId].data.name === topic;
        });
        if (existingTopicSegmentId === undefined) {
          this.setNewSegment(
            conversationTopicTimelineDataId,
            MarkerSegmentData,
            labelSegment,
            'creating topic label',
            'Topic label created.',
            { ignoresReadonly: true },
          );
        } else {
          this.setSegment(
            new TimelineSegmentId(conversationTopicTimelineDataId, existingTopicSegmentId),
            MarkerSegmentData,
            labelSegment,
            'updating topic label',
            'Topic label updated.',
            { ignoresReadonly: true },
          );
          updatedConversationTopicSegmentIds.push(existingTopicSegmentId);
        }
      }
      let deletingConversationTopicTimelineSegmentIds = existingConversationTopicSegmentIds
        .filter(existingConversationTopicSegmentId => (!updatedConversationTopicSegmentIds.includes(existingConversationTopicSegmentId)))
        .map(deletingConversationTopicSegmentId => new TimelineSegmentId(conversationTopicTimelineDataId, deletingConversationTopicSegmentId))
      this.deleteSegments(deletingConversationTopicTimelineSegmentIds, { ignoresReadonly: true });
    },

    $_updateTableState(timelineSegmentItems, playTimeMsec) {
      updateScrollTarget.call(this, timelineSegmentItems, playTimeMsec);
      updateTableRowStyle.call(this, timelineSegmentItems, playTimeMsec);

      function updateScrollTarget(timelineSegmentItems, playTimeMsec) {
        let previousBeginMsec = -Infinity;
        let previousItemIdx = undefined;
        let itemIdx = this.$data.$_scrollTargetFirstRowIdx;
        if (itemIdx === undefined) itemIdx = 0;
        for (; itemIdx < timelineSegmentItems.length; ++itemIdx) {
          let beginMsec = timelineSegmentItems[itemIdx].beginMsec;
          if (playTimeMsec <= beginMsec) break;
          if (previousBeginMsec !== beginMsec) {
            previousItemIdx = itemIdx;
          }
          previousBeginMsec = beginMsec;
        }
        this.$data.$_scrollTargetFirstRowIdx = previousItemIdx;
        this.$data.$_scrollTargetLastRowIdx = (previousItemIdx === undefined)? undefined : (itemIdx - 1)
      }

      function updateTableRowStyle(timelineSegmentItems, playTimeMsec) {
        let previousMarkedRowIdcs = this.$data.$_markedRowIdcs;
        let markedRowIdcs = [];
        for (let itemIdx = 0; itemIdx < timelineSegmentItems.length; ++itemIdx) {
          let beginMsec = timelineSegmentItems[itemIdx].beginMsec;
          let endMsec = timelineSegmentItems[itemIdx].endMsec;
          if ((beginMsec <= playTimeMsec) && (playTimeMsec < endMsec)) {
            markedRowIdcs.push(itemIdx);
          }
          if (playTimeMsec < beginMsec) break;
        }
        let unmarkedRowIdcs = [];
        let newlyMarkedRowIdcs = []
        for (let previousMarkedRowIdx of previousMarkedRowIdcs) {
          if (!markedRowIdcs.includes(previousMarkedRowIdx)) unmarkedRowIdcs.push(previousMarkedRowIdx)
        }
        for (let markedRowIdx of markedRowIdcs) {
          if (!previousMarkedRowIdcs.includes(markedRowIdx)) newlyMarkedRowIdcs.push(markedRowIdx)
        }
        for (let rowIdx of unmarkedRowIdcs) {
          let tableRowElement = this.$refs.timelineLabelForCefrTable.getTableRowElement(rowIdx);
          tableRowElement.classList.toggle('primary', false);
          tableRowElement.classList.toggle('lighten-4', false);
        }
        for (let rowIdx of newlyMarkedRowIdcs) {
          let tableRowElement = this.$refs.timelineLabelForCefrTable.getTableRowElement(rowIdx);
          tableRowElement.classList.toggle('primary', true);
          tableRowElement.classList.toggle('lighten-4', true);
        }
        this.$data.$_markedRowIdcs = markedRowIdcs;
      }
    },
  },
};
</script>